/**
 * The two id grammars this CLI applies, and why they are two.
 *
 * A leaf module (no imports) because its consumers are load-bearing and one of
 * the duties is a security check:
 *
 *  - **The worktree scope** (`worktree-scope.ts`) needs a base id strict enough
 *    that a garbled entry reads as absent, so the routing tripwire fails open to
 *    "no scope" rather than wedging on corrupt state.
 *  - **URL path construction** (`data/helpers.ts`) needs EVERY id strict enough
 *    that it cannot carry a path separator or a dot segment into a request path.
 *  - **A base id** additionally has to be a value the API will actually accept,
 *    because the CLI states the rule to the user before the request is made.
 *
 * The third duty is what splits the module in two. A base id is constrained by
 * the API; the other ids the Data surface puts in a path — a `record_type`, a
 * `file_type`, a toolset slug — are not constrained by it at all, so one shared
 * grammar can only be wrong for one of them.
 */

/**
 * The longest base id the API will mint. Mirrors `SLUG_ID_MAX_LENGTH` in
 * `apps/rekor/backend/src/durable-objects/_shared/validation.ts`.
 */
export const BASE_ID_MAX_LENGTH = 128;

/**
 * The base-id grammar — a LOCAL COPY of `BASE_ID_PATTERN` in
 * `apps/rekor/backend/src/durable-objects/_shared/validation.ts`, which is the
 * authoritative rule and the file to read before touching this one.
 *
 * A copy rather than an import because app-side code may not import the data
 * product's tree (AGENTS.md §Structure). Until the rule moves into `@wayai/core`,
 * which both sides may import, the two are kept together only by being written to
 * match. Both directions of drift are live defects, and
 * neither is the safe one:
 *
 *  - Too LENIENT and the CLI accepts an id the API refuses. That was this
 *    module's state until the server rule landed: `wayai bases create acme.crm`
 *    passed here and took a 400 quoting a charset no CLI surface stated.
 *  - Too STRICT and the CLI refuses an id the API would mint — the leading `_`
 *    is the live instance, since operator probes already carry one — hiding a
 *    legitimate id behind an error the user cannot act on.
 *
 * So: letters, digits, `-` and `_`, with `-` barred from the first position (it
 * reads as a flag wherever an id is passed on a command line) and `_` admitted
 * there. Everything else is out; the far side's comment enumerates why per
 * character, and that enumeration is not repeated here. Note the two characters
 * this grammar dropped when it adopted the server's: `.` is gone, which is what
 * lets the traversal check fall out of the charset rather than needing the old
 * `(?!\.\.?$)` guard, and the ceiling fell from 256 to 128.
 */
const BASE_ID_RE = /^[A-Za-z0-9_][A-Za-z0-9_-]*$/;

/**
 * The base-id rule as one clause, for the messages that state it.
 *
 * Every user-facing message about a base id interpolates this rather than
 * spelling the charset again: a message that describes a rule the validator does
 * not apply is the defect that shipped last time, and it can only recur if a
 * second copy of the sentence exists. Worded to match the API's own 400 so a
 * user who hits both reads one rule, not two.
 */
export const BASE_ID_RULE = `letters, digits, \`-\` and \`_\`, starting with a letter, digit or \`_\`, ${BASE_ID_MAX_LENGTH} characters or fewer`;

/** True when `id` is a structurally valid base id — the API's rule, locally. */
export function isValidBaseId(id: string): boolean {
  // Length before pattern, matching the far side: never run a regex over an
  // unbounded caller-supplied string. `length > 0` is parity with the far side's
  // `matchesSlugId`, not logic — `BASE_ID_RE` already requires a first character.
  return id.length > 0 && id.length <= BASE_ID_MAX_LENGTH && BASE_ID_RE.test(id);
}

/**
 * The grammar for an id the DATA PRODUCT does not constrain, on its way into a
 * request path.
 *
 * Deliberately wider than `isValidBaseId`, and it must stay that way. A
 * `record_type`, `file_type`, `relationship_type` or trigger id is created
 * server-side as `input.id ?? uuidv7()` with no charset check anywhere, so
 * `acme.contact` is an id the API accepts and returns. Validating those against
 * the base rule would refuse — from the client, before any request — a record
 * type the caller can otherwise read, write and list, which is the "CLI stricter
 * than the server" failure in its other direction.
 *
 * What it does still enforce is the one duty that is this side's own: the value
 * becomes a URL path segment (and, in `config-as-code`, a filename), so no
 * whitespace, no path separator, no bare dot segment, bounded length. The
 * leading `(?!\.\.?$)` rejects `.` and `..`: the character class already blocks
 * `/`, but a bare `..` is still a traversal segment — `path.join` would resolve
 * it to the parent directory, and `fetch()`'s URL parser resolves it in a
 * request path. `encodeURIComponent` does not neutralize either.
 */
const PATH_SAFE_ID_RE = /^(?!\.\.?$)[A-Za-z0-9._-]{1,256}$/;

/**
 * The path-safe rule as one clause, for the messages that state it.
 *
 * Exists for the same reason as `BASE_ID_RULE`, and the omission it corrects is
 * the same defect in miniature: the three messages that stated this rule each
 * spelled it differently, and all three said dot was allowed without saying that
 * a BARE `.` or `..` is not — so `wayai bases use ..` printed a rule its own
 * input satisfies, leaving the user nothing to change. None stated the cap.
 */
export const PATH_SAFE_ID_RULE =
  'letters, digits, dot, dash and underscore, with no path separators, not `.` or `..` alone, 256 characters or fewer';

/** True when `id` is safe to interpolate as a single path segment. */
export function isPathSafeId(id: string): boolean {
  return PATH_SAFE_ID_RE.test(id);
}
